import type { ApiErrorBody, ErrorCode } from '@dataroom/core';

/** Error thrown for every non-2xx response; mirrors the API error body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field issues of a `validation_failed` error, keyed by path (e.g. `values.amount`). */
  get issues(): Record<string, string> {
    const list =
      (this.details as { issues?: Array<{ path: string; message: string }> } | undefined)?.issues ?? [];
    return Object.fromEntries(list.map((i) => [i.path, i.message]));
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined | string[]>;

export type HttpOptions = {
  /** Default `/api/v1` (same origin; the Vite dev server proxies it). */
  baseUrl?: string;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
  /** Called on 401 (e.g. redirect to /login). */
  onUnauthenticated?: (err: ApiError) => void;
};

export type RequestOptions = {
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export type Http = {
  request<T>(method: string, path: string, opts?: RequestOptions): Promise<T>;
  get<T>(path: string, query?: QueryParams, signal?: AbortSignal): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  delete<T = void>(path: string): Promise<T>;
};

export function buildQuery(query?: QueryParams): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Path segment encoder: `seg\`/objects/${key}/records/${id}\`` */
export function seg(strings: TemplateStringsArray, ...values: Array<string | number>): string {
  return strings.reduce(
    (acc, s, i) => acc + s + (i < values.length ? encodeURIComponent(String(values[i])) : ''),
    '',
  );
}

export function createHttp(options: HttpOptions = {}): Http {
  const baseUrl = options.baseUrl ?? '/api/v1';
  const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));

  async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const hasBody = opts.body !== undefined;
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${path}${buildQuery(opts.query)}`, {
        method,
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'x-dataroom-client': 'web',
          ...(hasBody ? { 'content-type': 'application/json' } : {}),
          ...options.headers,
          ...opts.headers,
        },
        body: hasBody ? JSON.stringify(opts.body) : null,
        signal: opts.signal ?? null,
      });
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') throw err;
      throw new ApiError(0, 'network_error', 'Could not reach the server');
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const body = data as Partial<ApiErrorBody> | undefined;
      const err = new ApiError(
        res.status,
        body?.error?.code ?? 'internal',
        body?.error?.message ?? res.statusText,
        body?.error?.details,
        body?.error?.requestId,
      );
      if (res.status === 401) options.onUnauthenticated?.(err);
      throw err;
    }
    return data as T;
  }

  return {
    request,
    get: (path, query, signal) => request('GET', path, { query, ...(signal ? { signal } : {}) }),
    post: (path, body) => request('POST', path, { body }),
    patch: (path, body) => request('PATCH', path, { body }),
    put: (path, body) => request('PUT', path, { body }),
    delete: (path) => request('DELETE', path),
  };
}
